import { useWatch, type Control } from "react-hook-form";
import { Card } from "@/components/ui/Card";
import {
  calculateItemAmount,
  decimalToCents,
  decimalToMilli,
  formatCurrency,
  parseDuration,
} from "@/domain/billing";
import {
  resolvedUnitLabel,
  type InvoiceFormValues,
} from "@/features/invoices/invoice-form";

export function InvoiceSummary({
  control,
}: {
  control: Control<InvoiceFormValues>;
}) {
  const values = useWatch({ control });
  const currency = values.currency ?? "USD";
  const items = values.items ?? [];

  const subtotal = items.reduce((sum, item) => {
    const billingType = item?.billingType ?? "fixed";

    const unitPreset =
      item?.unitPreset ??
      (billingType === "hourly"
        ? "hour"
        : billingType === "quantity"
          ? "item"
          : "project");

    const unitLabel = resolvedUnitLabel({
      billingType,
      unitPreset,
      customUnitLabel: item?.customUnitLabel ?? "",
    });

    return (
      sum +
      calculateItemAmount({
        id: item?.id ?? "",
        description: item?.description ?? "",
        billingType,
        durationSeconds:
          billingType === "hourly"
            ? parseDuration(item?.duration ?? "") || 0
            : 0,
        hourlyRateCents:
          billingType === "hourly"
            ? decimalToCents(item?.hourlyRate ?? 0)
            : 0,
        quantityMilli:
          billingType === "quantity"
            ? decimalToMilli(item?.quantity ?? 0)
            : 0,
        unitLabel,
        unitPriceCents:
          billingType === "quantity"
            ? decimalToCents(item?.unitPrice ?? 0)
            : 0,
        fixedAmountCents:
          billingType === "fixed"
            ? decimalToCents(item?.fixedAmount ?? 0)
            : 0,
      })
    );
  }, 0);

  const taxRate = Math.min(
    100,
    Math.max(0, Number(values.taxRatePercent ?? 0)),
  );

  const tax = Math.round((subtotal * taxRate) / 100);
  const total = subtotal + tax;

  return (
    <Card className="overflow-hidden">
      <div className="bg-ink px-5 py-4 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-300">
          Live total
        </p>
        <p className="mt-2 text-3xl font-black">
          {formatCurrency(total, currency)}
        </p>
      </div>

      <div className="space-y-3 p-5 text-sm">
        <div className="flex justify-between gap-4 text-ink-muted">
          <span>Subtotal</span>
          <span className="font-bold text-ink">
            {formatCurrency(subtotal, currency)}
          </span>
        </div>

        <div className="flex justify-between gap-4 text-ink-muted">
          <span>Tax ({taxRate.toFixed(2)}%)</span>
          <span className="font-bold text-ink">
            {formatCurrency(tax, currency)}
          </span>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex justify-between gap-4">
            <span className="font-black text-ink">
              Grand total
            </span>
            <span className="font-black text-gold-700">
              {formatCurrency(total, currency)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
